import React, { useState, useEffect, useRef } from "react";
import { addMinutes, parse, format as formatDate } from "date-fns";

const TimePickerDropdown = ({ value, onChange, isStart = true, startTime }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const selectedItemRef = useRef(null);

	const generateTimeOptions = () => {
		const options = [];
		const baseDate = new Date(2024, 0, 1); // Use a fixed date for time calculations

		for (let hour = 0; hour < 24; hour++) {
			for (let minute = 0; minute < 60; minute += 15) {
				const currentTime = new Date(baseDate);
				currentTime.setHours(hour, minute);

				// For end time, filter options based on start time
				if (!isStart && startTime) {
					const startDate = parse(startTime, "HH:mm", baseDate);
					const minEndDate = addMinutes(startDate, 15);

					if (currentTime < minEndDate) {
						continue;
					}
				}

				const formattedHour = hour % 12 || 12;
				const period = hour < 12 ? "AM" : "PM";
				const formattedMinute = minute.toString().padStart(2, "0");
				const timeString = `${formattedHour}:${formattedMinute} ${period}`;
				const timeValue = `${hour
					.toString()
					.padStart(2, "0")}:${formattedMinute}`;

				options.push({ label: timeString, value: timeValue });
			}
		}
		return options;
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		if (isOpen && selectedItemRef.current) {
			selectedItemRef.current.scrollIntoView({
				block: "center",
				behavior: "auto",
			});
		}
	}, [isOpen]);

	const timeOptions = generateTimeOptions();
	const selectedTime =
		timeOptions.find((opt) => opt.value === value)?.label || value;

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none"
			>
				{selectedTime}
			</button>

			{isOpen && (
				<div className="absolute z-50 mt-1 max-h-60 w-32 overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600">
					{timeOptions.map((option) => (
						<div
							key={option.value}
							ref={option.value === value ? selectedItemRef : null}
							className={`cursor-pointer px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 
                ${
									value === option.value
										? "bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
										: "text-gray-900 dark:text-gray-100"
								}`}
							onClick={() => {
								onChange(option.value);
								setIsOpen(false);
							}}
						>
							{option.label}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default TimePickerDropdown;
